import { useEffect } from 'react';
import { AuthUnavailableModal, ReauthModal } from '@brew/ui';
import { auth, setReauthSignoutHandler } from './reauthClient';
import { useAuth } from './useAuth';
import { useUnsavedModelGuard } from './useUnsavedModelGuard';

// Wires the shared presentational modals to the app.
//
// ReauthModal: state from the controller, logout that confirms discarding
// unsaved model changes (the prompt promises "your work is still here"; only
// signing in again keeps it, #2272), then cancels the pending re-auth prompt
// and runs the app's brewauth (OIDC) signout. The embedded sign-in card's own
// "Sign out" runs that same logout; the controller has already cancelled the
// prompt by then. The frame host and "Retry now" go straight to the
// controller (@brew/ui 0.21.0 embeddedReauth).
//
// AuthUnavailableModal (@brew/ui 0.17.0, jorigin/brewauth#98): shown while
// brewauth cannot be reached — a 503 `X-Auth-Status: UNAVAILABLE` from the API,
// or a silent refresh that cannot reach brewauth. `auth.attachTo(apiClient)`
// already holds and replays those requests; without this mount the hold still
// works but the user sees nothing while it waits.
export default function ReauthModalHost() {
  const state = auth.reauth.useState();
  const unavailable = auth.unavailable.useState();
  const { handleLogout } = useAuth();
  const { guard, dialog } = useUnsavedModelGuard();
  useEffect(
    () => setReauthSignoutHandler(() => guard(() => void handleLogout())),
    [guard, handleLogout],
  );
  return (
    <>
      <ReauthModal
        {...state}
        onSignIn={auth.reauth.signIn}
        onLogout={() => guard(() => {
          auth.reauth.cancel();
          void handleLogout();
        })}
        onAttachFrameHost={auth.reauth.attachFrameHost}
        onRetryConnection={auth.reauth.retryConnection}
      />
      {dialog}
      <AuthUnavailableModal {...unavailable} onRetry={auth.unavailable.retryNow} />
    </>
  );
}
